import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Sparkles, Lock } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toISO, monthRange, fmtINR, prettyDate } from "../lib/dates";
import { Calendar } from "../components/ui/calendar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";

const VILLA_ID = "apukaa-lonavla";

export default function ApukaaInventory() {
  const { user } = useAuth();
  const [month, setMonth] = useState(new Date());
  const [data, setData] = useState({ days: [], base_rate: null, can_edit: false, can_book: false, villa_title: "" });
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [editForm, setEditForm] = useState({ status: "available", rate: "", guest_name: "", note: "" });
  const [suggestion, setSuggestion] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    const { start, end } = monthRange(month);
    api.get(`/inventory/${VILLA_ID}`, { params: { start, end } })
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Failed to load availability"));
  }, [month]);

  useEffect(() => { load(); }, [load, user]);

  const byDate = useMemo(() => {
    const m = {};
    (data.days || []).forEach((d) => { m[d.date] = d; });
    return m;
  }, [data]);

  const blockedDays = (data.days || []).filter((d) => d.status === "blocked").map((d) => new Date(d.date + "T00:00:00"));
  const bookedDays = (data.days || []).filter((d) => d.status === "booked").map((d) => new Date(d.date + "T00:00:00"));

  const onDayClick = (day) => {
    const iso = toISO(day);
    const entry = byDate[iso];
    setSelected(iso);
    setSuggestion(null);
    setEditForm({
      status: entry?.status || "available",
      rate: entry?.rate || "",
      guest_name: entry?.guest_name || "",
      note: entry?.note || "",
    });
    setOpen(true);
  };

  const fetchSuggestion = async () => {
    try {
      const { data } = await api.get(`/inventory/${VILLA_ID}/suggest-rate`, { params: { date: selected } });
      setSuggestion(data);
      setEditForm((f) => ({ ...f, rate: data.suggested_rate }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveAdmin = async () => {
    setSaving(true);
    try {
      await api.put(`/inventory/${VILLA_ID}/${selected}`, {
        status: editForm.status,
        rate: editForm.rate ? Number(editForm.rate) : null,
        guest_name: editForm.guest_name,
        note: editForm.note,
      });
      toast.success("Availability updated");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const bookPartner = async () => {
    setSaving(true);
    try {
      await api.post(`/inventory/${VILLA_ID}/book`, {
        date: selected,
        guest_name: editForm.guest_name || "Guest",
        note: editForm.note,
      });
      toast.success("Date booked");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const selectedEntry = selected ? byDate[selected] : null;
  const rateForSelected = selectedEntry?.rate;

  return (
    <div data-testid="apukaa-inventory-page" className="bg-secondary/40 min-h-screen">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-accent">Rent-O-Villa</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-light mt-2">{data.villa_title || "Apukaa Villa"} — Availability</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          Check open dates and nightly rates. {data.can_edit ? "As an owner/admin you can block dates and set prices." : data.can_book ? "As a partner you can book available dates." : "Bookings are managed by LAVista; blocked dates cannot be booked."}
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
          <div className="lg:col-span-2 bg-card border border-border rounded-lg p-5" data-testid="inventory-calendar">
            <Calendar
              mode="single"
              month={month}
              onMonthChange={setMonth}
              onDayClick={onDayClick}
              modifiers={{ blocked: blockedDays, booked: bookedDays }}
              modifiersClassNames={{
                blocked: "!bg-destructive/15 !text-destructive line-through",
                booked: "!bg-accent/20 !text-accent font-semibold",
              }}
              className="w-full"
            />
            <div className="flex flex-wrap gap-5 mt-4 pt-4 border-t border-border text-sm">
              <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-sm bg-background border border-border" /> Available</span>
              <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-sm bg-accent/40" /> Booked</span>
              <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-sm bg-destructive/30" /> Blocked</span>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-6 h-fit" data-testid="inventory-side-panel">
            <h3 className="font-heading text-2xl font-light">Base rate</h3>
            <p className="font-heading text-3xl text-accent mt-1">{fmtINR(data.base_rate)} <span className="text-sm text-muted-foreground">/ night</span></p>
            <p className="text-sm text-muted-foreground mt-3">
              Nightly prices adjust automatically for peak season (monsoon &amp; December) and weekends.
            </p>
            {!user && (
              <div className="mt-6 bg-secondary rounded-md p-4 text-sm flex items-start gap-2">
                <Lock className="h-4 w-4 mt-0.5 text-accent" />
                <span>Booking partners can log in to reserve available dates.</span>
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-6">Click any date on the calendar to view details{data.can_edit ? " or manage it" : data.can_book ? " and book it" : ""}.</p>
          </div>
        </div>
      </div>

      {/* Day dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="inventory-day-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-light">{prettyDate(selected)}</DialogTitle>
          </DialogHeader>

          {/* Read-only summary */}
          <div className="text-sm">
            <div className="flex justify-between py-1"><span className="text-muted-foreground">Status</span><span className="capitalize font-medium">{selectedEntry?.status || "available"}</span></div>
            <div className="flex justify-between py-1"><span className="text-muted-foreground">Rate</span><span className="font-medium">{rateForSelected ? fmtINR(rateForSelected) : `${fmtINR(data.base_rate)} (base)`}</span></div>
          </div>

          {data.can_edit && (
            <div className="space-y-4 border-t border-border pt-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger data-testid="edit-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="blocked">Blocked</SelectItem>
                    <SelectItem value="booked">Booked</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Nightly rate (optional override)</Label>
                  <Button type="button" size="sm" variant="ghost" data-testid="suggest-price-btn" onClick={fetchSuggestion} className="gap-1 h-7 text-accent">
                    <Sparkles className="h-3.5 w-3.5" /> Suggest
                  </Button>
                </div>
                <Input type="number" data-testid="edit-rate-input" value={editForm.rate} onChange={(e) => setEditForm({ ...editForm, rate: e.target.value })} placeholder={`${data.base_rate} (base)`} />
                {suggestion && (
                  <p className="text-xs text-accent" data-testid="suggestion-text">
                    Suggested {fmtINR(suggestion.suggested_rate)} — {suggestion.reason}
                  </p>
                )}
              </div>
              {editForm.status === "booked" && (
                <div className="space-y-2">
                  <Label>Guest name</Label>
                  <Input data-testid="edit-guest-input" value={editForm.guest_name} onChange={(e) => setEditForm({ ...editForm, guest_name: e.target.value })} placeholder="Guest / partner" />
                </div>
              )}
              <div className="space-y-2">
                <Label>Note</Label>
                <Textarea data-testid="edit-note-input" value={editForm.note} onChange={(e) => setEditForm({ ...editForm, note: e.target.value })} rows={2} />
              </div>
              <Button onClick={saveAdmin} disabled={saving} data-testid="save-inventory-btn" className="w-full rounded-full">
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          )}

          {!data.can_edit && data.can_book && (
            <div className="space-y-4 border-t border-border pt-4">
              {selectedEntry?.status && selectedEntry.status !== "available" ? (
                <p className="text-sm text-destructive">This date is {selectedEntry.status} and cannot be booked.</p>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>Guest name</Label>
                    <Input data-testid="partner-guest-input" value={editForm.guest_name} onChange={(e) => setEditForm({ ...editForm, guest_name: e.target.value })} placeholder="Guest name" />
                  </div>
                  <div className="space-y-2">
                    <Label>Note</Label>
                    <Textarea data-testid="partner-note-input" value={editForm.note} onChange={(e) => setEditForm({ ...editForm, note: e.target.value })} rows={2} />
                  </div>
                  <Button onClick={bookPartner} disabled={saving} data-testid="partner-book-btn" className="w-full rounded-full">
                    {saving ? "Booking..." : "Book this date"}
                  </Button>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
